import type { DowngradeReason } from '@/api/planApi';

const KNOWN_DOWNGRADE_REASONS: Array<DowngradeReason> = [
  'FREE_PLAN', 'STARTER_PLAN', 'MONTHLY_LIMIT_EXCEEDED', 'PENDING_UPGRADE', 'NOT_OWNER',
];
const KNOWN_CHANGE_PLAN_REASONS = ['ENTERPRISE_ACTIVE'];

export interface PersonalPlanOverlayFields {
  monthlyLimit: number;
  billingCycleDay: number | null;
  canDowngrade: boolean;
  downgradeReason: DowngradeReason;
  changePlanReason: string | null;
}

function isNullableString(value: any): boolean {
  return value === null || typeof value === 'string';
}

function isNullableNumber(value: any): boolean {
  return value === null || (typeof value === 'number' && !Number.isNaN(value));
}

/**
 * workspaceApi.getMyPlan() 응답을 PlanPage.tsx의 개인 컨텍스트 표시용 overlay로
 * 검증·변환한다. 이 함수가 다루는 필드는 monthlyLimit/billingCycleDay/canDowngrade/
 * downgradeReason/changePlanReason 뿐이다 - plan(currentPlan)과 enterpriseContract는
 * 여기서 절대 다루지 않는다:
 * - plan은 업그레이드/다운그레이드 중 어느 쪽인지 결정하는 값이라(handleChangePlan),
 *   overlay가 legacy와 미세하게라도 어긋나면 잘못된 변경 API 분기를 탈 위험이 있다.
 * - enterpriseContract(planEnterpriseContract)는 PlanPage.tsx의 handleChangePlan이
 *   "계약 활성 상태면 일반 플랜 변경 차단" 게이트로 직접 참조하므로, 표시 전용이
 *   아니라 실제 차단 로직의 입력이다.
 * 두 필드 모두 legacy PlanInfo(planApi.getMyPlan())만 신뢰한다.
 *
 * 반환된 5개 필드는 전부 PlanPage.tsx 렌더링(JSX)에만 쓰이고 handleChangePlan/
 * handleConfirmChange 등 실제 변경 요청 핸들러는 legacy planInfo를 직접 읽으므로
 * 이 값이 틀려도 최악의 경우 UI 표시 문구/버튼 활성화 상태가 legacy와 잠깐
 * 어긋나는 정도이지, 잘못된 변경 요청이 서버로 나가지는 않는다.
 *
 * all-or-nothing 검증: 응답이 객체가 아니거나 필드 하나라도 이상하면 null을
 * 반환해 legacy 그대로 렌더링되게 한다.
 */
export function validateAndBuildPersonalPlanOverlay(row: unknown): PersonalPlanOverlayFields | null {
  if (row == null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;

  if (typeof r.monthlyLimit !== 'number' || Number.isNaN(r.monthlyLimit)) return null;
  if (!isNullableNumber(r.billingCycleDay)) return null;
  if (typeof r.canDowngrade !== 'boolean') return null;
  if (r.downgradeReason !== null && !KNOWN_DOWNGRADE_REASONS.includes(r.downgradeReason as DowngradeReason)) return null;
  if (r.changePlanReason !== null && !KNOWN_CHANGE_PLAN_REASONS.includes(r.changePlanReason as string)) return null;
  if (!isNullableString(r.changePlanReason)) return null;

  return {
    monthlyLimit: r.monthlyLimit as number,
    billingCycleDay: r.billingCycleDay as number | null,
    canDowngrade: r.canDowngrade as boolean,
    downgradeReason: r.downgradeReason as DowngradeReason,
    changePlanReason: r.changePlanReason as string | null,
  };
}
